/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from '@/components/link';
import { cn } from '@/lib/utils';
// RGBARRY-CUSTOMIZATION (W-23789028 consent): TrueVault owns the authoritative Privacy Policy
// when configured, so the checkout footer must point at the same hosted policy as the main footer.
import { useTrueVaultPrivacyLinks } from '@/components/truevault-notices/use-truevault-privacy-links';

export default function LegalLinks({ className }: { className?: string }): ReactElement {
    const { t } = useTranslation('footer');
    const privacyLinks = useTrueVaultPrivacyLinks();
    // RGBARRY-CUSTOMIZATION (perf): prefetch="none" on the footer legal links below. These sit
    // in/near the viewport, so React Router's default intent prefetch fired on pointerover and
    // showed up as avoidable TBT; the links are low-intent, so we opt them out. Mirrors the same
    // change in main-footer.tsx.
    return (
        <div className={cn('flex items-center gap-4', className)}>
            {/* RGBARRY-CUSTOMIZATION: Privacy Policy → TrueVault hosted policy when configured
                (authoritative; matches main footer), else the /privacy-policy.html content asset
                (200). The template's bare /privacy placeholder 404s and is never used. External
                links must be a plain <a> (the site-context Link would prefix the path). */}
            {privacyLinks ? (
                <a
                    href={privacyLinks.privacyPolicy}
                    rel="noopener noreferrer"
                    className="hover:text-foreground transition-colors">
                    {t('links.privacyPolicy')}
                </a>
            ) : (
                <Link to="/privacy-policy.html" prefetch="none" className="hover:text-foreground transition-colors">
                    {t('links.privacyPolicy')}
                </Link>
            )}
            {/* RGBARRY-CUSTOMIZATION: point at the prod content-asset path (/terms-of-use.html)
                instead of the template's bare /terms placeholder, which has no route/content
                and 404s. See main-footer.tsx:344 — other legal links already use prod .html paths. */}
            <Link to="/terms-of-use.html" prefetch="none" className="hover:text-foreground transition-colors">
                {t('links.termsOfUse')}
            </Link>
        </div>
    );
}
